import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { callAPI, callAPIWithoutAuth } from '../../utils/apiUtils';
import { apiUrls } from '../../utils/apiUrls';
import { fetchProductById } from './productDetailSlice';

export const fetchFilterOptions = createAsyncThunk(
    'products/fetchFilterOptions',
    async (_, { rejectWithValue }) => {
        try {
            const [categoriesRes, colorsRes, sizesRes] = await Promise.all([
                callAPIWithoutAuth(apiUrls.categoryListing, {}, "GET", {}),
                callAPIWithoutAuth(apiUrls.colorsListing, {}, "GET", {}),
                callAPIWithoutAuth(apiUrls.sizesListing, {}, "GET", {})
            ]);

            return {
                categories: categoriesRes?.data?.success ? categoriesRes.data.data : [],
                colors: colorsRes?.data?.success ? colorsRes.data.data : [],
                sizes: sizesRes?.data?.success ? sizesRes.data.data : []
            };
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Network error');
        }
    }
);

export const productFav = createAsyncThunk(
    'products/favItem',
    async (product_id, { rejectWithValue, dispatch, getState }) => {
        try {
            const state = getState();
            const userId = state.auth?.user?.id;
            const response = await callAPI(
                apiUrls.productSave,
                {},
                "POST",
                { product_id }
            );

            if (response?.data?.success) {
                const currentFilters = state.products.filters;
                const currentPage = state.products.currentPage;
                dispatch(fetchProducts({
                    ...currentFilters,
                    page: currentPage,
                    userId: userId
                }));

                if (state.products.favoriteListing?.data?.length > 0) {
                    dispatch(fetchFavListing());
                }

                const currentProductDetail = state.details?.product;
                if (currentProductDetail && currentProductDetail.id === product_id) {
                    dispatch(fetchProductById(product_id));
                }
                return {
                    product_id,
                    is_saved: response.data.is_saved || false,
                    data: response.data.data,
                    message: response.data.message
                };
            } else {
                return rejectWithValue(response?.data?.message || 'Failed to fetch categories');
            }
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Network error');
        }
    }
);

export const fetchFavListing = createAsyncThunk(
    'product/favListing',
    async (limit = null, { rejectWithValue }) => {
        try {
            const params = {}
            if (limit != null) {
                params.limit = limit
            }
            const response = await callAPI(
                apiUrls.productSaveListing,
                params,
                "GET",
                {}
            );

            if (response?.data?.success) {
                return response.data.data;
            } else {
                return rejectWithValue(response?.data?.message || 'Failed to fetch categories');
            }
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Network error');
        }
    }
);

export const fetchProducts = createAsyncThunk(
    'products/fetchProducts',
    async (filters = {}, { rejectWithValue, getState }) => {
        try {
            const state = getState();
            const userId = state.auth?.user?.id;
            const params = new URLSearchParams();

            params.append('page', filters.page || 1);

            if (filters.search) {
                params.append('search', filters.search);
            }

            if (filters.is_featured) {
                params.append('is_featured', filters.is_featured);
                params.append('per_page', 1000);
            }
            if (!filters.is_featured) {
                params.append('per_page', 9);
            }

            if (filters.sort) {
                params.append('sort_by', filters.sort);
            }

            if (filters.categories && Object.keys(filters.categories).length > 0) {
                Object.keys(filters.categories).forEach(key => {
                    if (filters.categories[key]) {
                        const categoryId = Number(key);
                        if (!isNaN(categoryId) && categoryId > 0) {
                            params.append('category_id[]', categoryId);
                        } else {
                            console.warn('Invalid category ID skipped:', key);
                        }
                    }
                });
            }

            if (filters.gender && Object.keys(filters.gender).length > 0) {
                Object.keys(filters.gender).forEach(key => {
                    if (filters.gender[key]) {
                        params.append('gender[]', key);
                    }
                });
            }

            if (filters.occasion && Object.keys(filters.occasion).length > 0) {
                Object.keys(filters.occasion).forEach(key => {
                    if (filters.occasion[key]) {
                        params.append('occasion[]', key);
                    }
                });
            }

            if (filters.sizes && Object.keys(filters.sizes).length > 0) {
                Object.keys(filters.sizes).forEach(key => {
                    if (filters.sizes[key]) {
                        params.append('size_id[]', parseInt(key));
                    }
                });
            }

            if (filters.colors && filters.colors.length > 0) {
                filters.colors.forEach(color => {
                    if (typeof color === 'string') {
                        params.append('color_id[]', parseInt(color));
                    } else if (typeof color === 'object' && color.id) {
                        params.append('color_id[]', parseInt(color.id));
                    } else {
                        params.append('color_id[]', parseInt(color));
                    }
                });
            }

            if (filters.priceRange) {
                if (filters.priceRange.min !== undefined && filters.priceRange.min !== 0) {
                    params.append('price_min', filters.priceRange.min);
                }
                if (filters.priceRange.max !== undefined && filters.priceRange.max !== 7500) {
                    params.append('price_max', filters.priceRange.max);
                }
            }

            const queryString = params.toString();

            const response = await callAPIWithoutAuth(
                `${apiUrls.productListing}/${userId}?${queryString}`,
                {},
                "GET",
                {}
            );

            if (response?.data?.success) {
                return response.data.data;
            } else {
                return rejectWithValue(response?.data?.message || 'Failed to fetch products');
            }
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Network error');
        }
    }
);

const productSlice = createSlice({
    name: 'products',
    initialState: {
        products: [],
        loading: false,
        shouldShowLoader: false,
        error: null,
        totalResults: 0,
        from: 0,
        to: 0,
        currentPage: 1,
        totalPages: 1,
        filters: {
            categories: {},
            gender: {},
            occasion: {},
            priceRange: { min: 0, max: 7500 },
            sizes: {},
            colors: [],
            sort: 'newest'
        },
        filterOptions: {
            categories: [],
            colors: [],
            sizes: [],
            loading: false,
            error: null
        },
        favorites: [],
        favoriting: false,
        favoriteError: null,
        favoriteListing: {
            data: [],
            loading: false,
            error: null,
            totalResults: 0,
            from: 0,
            to: 0,
            currentPage: 1,
            totalPages: 1,
            perPage: 10
        }
    },
    reducers: {
        setFilters: (state, action) => {
            state.filters = { ...state.filters, ...action.payload };
        },
        updateFilter: (state, action) => {
            const { filterType, key, value } = action.payload;

            switch (filterType) {
                case 'categories':
                case 'gender':
                case 'occasion':
                case 'sizes':
                    if (!state.filters[filterType]) state.filters[filterType] = {};
                    const normalizedKey = filterType === 'categories' ? Number(key) : key;
                    if (filterType === 'categories' && (isNaN(normalizedKey) || normalizedKey <= 0)) {
                        break;
                    }
                    state.filters[filterType][normalizedKey] = value;
                    if (value === false) {
                        delete state.filters[filterType][normalizedKey];
                    }
                    break;

                case 'priceRange':
                    if (key === 'slider') {
                        state.filters.priceRange = value;
                    } else {
                        if (!state.filters.priceRange) {
                            state.filters.priceRange = { min: 0, max: 7500 };
                        }
                        state.filters.priceRange[key] = value;

                        if (key === 'min' && value > (state.filters.priceRange.max || 7500)) {
                            state.filters.priceRange.max = value;
                        }
                        if (key === 'max' && value < (state.filters.priceRange.min || 0)) {
                            state.filters.priceRange.min = value;
                        }
                    }
                    break;

                case 'colors':
                    if (!state.filters.colors) state.filters.colors = [];
                    if (value) {
                        if (!state.filters.colors.includes(key)) {
                            state.filters.colors.push(key);
                        }
                    } else {
                        state.filters.colors = state.filters.colors.filter(color => color !== key);
                    }
                    break;

                case 'search':
                    state.filters.search = value;
                    break;

                case 'clearAll':
                    state.filters = {
                        categories: {},
                        gender: {},
                        occasion: {},
                        priceRange: { min: 0, max: 7500 },
                        sizes: {},
                        colors: [],
                        sort: 'newest'
                    };
                    break;

                default:
                    break;
            }
        },
        setShouldShowLoader: (state, action) => {
            state.shouldShowLoader = action.payload;
        },
        setCategoryFilter: (state, action) => {
            const { categoryId, value = true } = action.payload;
            if (!state.filters.categories) {
                state.filters.categories = {};
            }
            state.filters.categories = { [categoryId]: value };
        },
        setSort: (state, action) => {
            state.filters.sort = action.payload;
        },
        setPage: (state, action) => {
            state.currentPage = action.payload;
        },
        setSearch: (state, action) => {
            state.filters.search = action.payload;
        },
        setFilterOptions: (state, action) => {
            state.filterOptions = { ...state.filterOptions, ...action.payload };
        },
        clearProductsError: (state) => {
            state.error = null;
        },
        toggleFavorite: (state, action) => {
            const productId = action.payload;
            const index = state.favorites.indexOf(productId);
            if (index === -1) {
                state.favorites.push(productId);
            } else {
                state.favorites.splice(index, 1);
            }
        },
        clearFavoriteListing: (state) => {
            state.favoriteListing = {
                data: [],
                loading: false,
                error: null,
                totalResults: 0,
                from: 0,
                to: 0,
                currentPage: 1,
                totalPages: 1,
                perPage: 10
            };
        },
        setFavoriteListingPage: (state, action) => {
            state.favoriteListing.currentPage = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchProducts.pending, (state) => {
                if (state.shouldShowLoader || state.products.length === 0) {
                    state.loading = true;
                }
                state.error = null;
            })
            .addCase(fetchProducts.fulfilled, (state, action) => {
                state.loading = false;
                state.shouldShowLoader = false;
                state.products = action.payload.data || [];
                state.totalResults = action.payload.total || 0;
                state.from = action.payload.from || 0;
                state.to = action.payload.to || 0;
                state.currentPage = action.payload.current_page || 1;
                state.totalPages = action.payload.last_page || 1;
            })
            .addCase(fetchProducts.rejected, (state, action) => {
                state.loading = false;
                state.shouldShowLoader = false;
                state.error = action.payload;
            })
            .addCase(fetchFilterOptions.pending, (state) => {
                state.filterOptions.loading = true;
                state.filterOptions.error = null;
            })
            .addCase(fetchFilterOptions.fulfilled, (state, action) => {
                state.filterOptions.loading = false;
                state.filterOptions.categories = action.payload.categories || [];
                state.filterOptions.colors = action.payload.colors || [];
                state.filterOptions.sizes = action.payload.sizes || [];
            })
            .addCase(fetchFilterOptions.rejected, (state, action) => {
                state.filterOptions.loading = false;
                state.filterOptions.error = action.payload;
            })
            .addCase(productFav.pending, (state) => {
                state.favoriting = true;
                state.favoriteError = null;
            })
            .addCase(productFav.fulfilled, (state, action) => {
                state.favoriting = false;
                const { product_id, is_saved } = action.payload;

                state.products = state.products.map(product => {
                    if (product.id === product_id) {
                        return {
                            ...product,
                            is_favorite: is_saved
                        };
                    }
                    return product;
                });

                if (state.favoriteListing.data.length > 0) {
                    if (!is_saved) {
                        state.favoriteListing.data = state.favoriteListing.data.filter(
                            item => item.product_id !== product_id
                        );
                        state.favoriteListing.totalResults = Math.max(0, state.favoriteListing.totalResults - 1);
                        state.favoriteListing.to = Math.max(0, state.favoriteListing.to - 1);
                    }
                }
            })
            .addCase(productFav.rejected, (state, action) => {
                state.favoriting = false;
                state.favoriteError = action.payload;
            })
            .addCase(fetchFavListing.pending, (state) => {
                state.favoriteListing.loading = true;
                state.favoriteListing.error = null;
            })
            .addCase(fetchFavListing.fulfilled, (state, action) => {
                state.favoriteListing.loading = false;
                if (action.payload.data && Array.isArray(action.payload.data)) {
                    state.favoriteListing.data = action.payload.data.map(item => ({
                        ...item,
                        product: item.product
                    }));
                    state.favoriteListing.totalResults = action.payload.total || 0;
                    state.favoriteListing.from = action.payload.from || 0;
                    state.favoriteListing.to = action.payload.to || 0;
                    state.favoriteListing.currentPage = action.payload.current_page || 1;
                    state.favoriteListing.totalPages = action.payload.last_page || 1;
                    state.favoriteListing.perPage = action.payload.per_page || 10;
                } else if (Array.isArray(action.payload)) {
                    state.favoriteListing.data = action.payload;
                    state.favoriteListing.totalResults = action.payload.length;
                    state.favoriteListing.from = 1;
                    state.favoriteListing.to = action.payload.length;
                    state.favoriteListing.currentPage = 1;
                    state.favoriteListing.totalPages = 1;
                }
            })
            .addCase(fetchFavListing.rejected, (state, action) => {
                state.favoriteListing.loading = false;
                state.favoriteListing.error = action.payload;
            });
    }
});

export const {
    setFilters,
    updateFilter,
    setSort,
    setPage,
    setSearch,
    setFilterOptions,
    setCategoryFilter,
    clearProductsError,
    toggleFavorite,
    clearFavoriteListing,
     setShouldShowLoader,
    setFavoriteListingPage
} = productSlice.actions;

export default productSlice.reducer;